'use client'

import { getTestDetailsService } from '@/api/services/testService'
import { TestDetailsResponse } from '@/api/services/types/testTypes'
import PageWrap from '@/components/layout/containers/PageWrap'
import { Progress } from '@/components/progress'
import { Skeleton } from '@/components/skeleton'
import { Seo } from '@/components/specific/seo/Seo'
import CurrentTask from '@/components/tasks/CurrentTask'
import { TestResponseProvider } from '@/contexts/TestResponseContext'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { InstructionButton } from '../tasks/components/InstructionButton'
import StartCard from '../tasks/components/StartCard'

export default function TestSession() {
  const { id } = useParams<{ id: string }>()

  const { data, isLoading, error } = useQuery({
    queryKey: ['test', id],
    queryFn: async () => {
      if (!id) return null
      const response = await getTestDetailsService(id)
      if (!response.success) {
        console.error('Błąd API:', response.data)
        return null
      }
      return response.data
    }
  })

  const providerKey = data ? `${data.id}` : 'empty'

  return (
    <TestResponseProvider
      data={data ?? null}
      id={data?.id ?? null}
      key={providerKey}
      stringifyJson={JSON.stringify(data)}
    >
      <TestSessionView data={data} error={error} isLoading={isLoading} />
    </TestResponseProvider>
  )
}

function TestSessionView({
  data,
  isLoading,
  error
}: {
  data: TestDetailsResponse | null | undefined
  isLoading: boolean
  error: Error | null
}) {
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0)
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(-1)
  const [_, setBlockNext] = useState(false)

  const currentModule = data?.modules[currentModuleIndex]
  const questions = currentModule?.randomizeOrder
    ? currentModule.randomizedAndCountedQuestions
    : currentModule?.allQuestions || []

  const currentQuestion = questions[currentQuestionIndex]

  const handleStart = () => {
    setCurrentQuestionIndex(0)
  }

  const handleNext = () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1)
    } else if (currentModuleIndex < (data?.modules.length || 0) - 1) {
      setCurrentModuleIndex((prev) => prev + 1)
      setCurrentQuestionIndex(-1)
    }
  }

  if (isLoading) {
    return (
      <PageWrap>
        <div className="p-6">
          <Skeleton className="mb-4 h-8 w-1/3" />
          <Skeleton className="mb-2 h-4 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
        </div>
      </PageWrap>
    )
  }

  if (error || !data || !currentModule) {
    return (
      <PageWrap>
        <div className="p-6">
          <div className="text-red-500">Wystąpił błąd podczas ładowania testu</div>
        </div>
      </PageWrap>
    )
  }

  // Sprawdzenie czy mamy pytania w module
  if (questions.length === 0) {
    return (
      <PageWrap>
        <div className="p-6">
          <div className="text-red-500">Ten moduł nie zawiera żadnych pytań</div>
        </div>
      </PageWrap>
    )
  }

  return (
    <>
      <Seo />
      <PageWrap className="bg-gray-50">
        <div className="mx-auto flex h-full w-full flex-col">
          <h4 className="text-l p-4 font-bold">
            Moduł {currentModuleIndex + 1} z {data.modules.length} - Pytanie{' '}
            {currentQuestionIndex + 1} z {questions.length}
          </h4>
          <Progress
            className="w-full"
            max={questions.length}
            value={currentQuestionIndex + 1}
          />
          <div className="flex flex-col items-end justify-end gap-4 p-4">
            <InstructionButton onClick={() => {}} />
          </div>

          <div className="self-center pt-4 lg:max-w-[40vw]">
            {currentQuestionIndex === -1 ? (
              <StartCard
                description={currentModule.text || ''}
                onStart={handleStart}
                title={currentModule.name}
              />
            ) : (
              currentQuestion && (
                <CurrentTask
                  data={currentQuestion}
                  moduleId={currentModule.id}
                  onNext={handleNext}
                  onStart={() => setBlockNext(true)}
                  questionId={currentQuestion.id}
                  type={currentQuestion.questionType}
                />
              )
            )}
          </div>
        </div>
      </PageWrap>
    </>
  )
}
